import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, CheckCircle, Mail } from "lucide-react";
import { VoiceInput } from "../components/VoiceInput";
import { SEO } from "../components/SEO";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const ContactPage = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ticketId, setTicketId] = useState("");
  const [aiResponse, setAiResponse] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return toast.error("Vul alle verplichte velden in");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      setTicketId(res.data.ticket_id);
      setAiResponse(res.data.response || "");
      setSubmitted(true);
    } catch { toast.error("Versturen mislukt. Probeer het opnieuw."); }
    finally { setLoading(false); }
  };

  if (submitted) return (
    <div className="max-w-lg mx-auto px-4 py-16" data-testid="contact-success">
      <div className="text-center mb-6">
        <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-4" />
        <h1 className="text-2xl font-black mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>Bericht Ontvangen</h1>
        <p className="text-xs text-gray-400">Referentie: {ticketId}</p>
      </div>
      {aiResponse && (
        <div className="bg-white border border-[#E5E5E5] p-5 mb-4">
          <p className="text-xs font-bold uppercase tracking-wider text-[#2d8cf0] mb-2">Antwoord van ons Support Team</p>
          <p className="text-sm text-[#333] whitespace-pre-wrap leading-relaxed">{aiResponse}</p>
        </div>
      )}
      <p className="text-xs text-center text-gray-400">Niet tevreden met dit antwoord? Stuur een vervolgbericht en we escaleren naar een medewerker.</p>
    </div>
  );

  return (
    <div className="max-w-lg mx-auto px-4 py-12" data-testid="contact-page">
      <SEO
        title="Contact — Debate Engine"
        description="Vragen, feedback of een bug gevonden? Neem direct contact op met het Debate Engine support team. Antwoord binnen één werkdag."
        path="/contact"
      />
      <div className="text-center mb-8">
        <Mail className="w-8 h-8 mx-auto mb-3 text-[#2d8cf0]" />
        <h1 className="text-2xl font-black mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>Contact</h1>
        <p className="text-sm text-[#52525B]">Heb je een vraag, opmerking of feedback? Neem contact met ons op.</p>
      </div>

      <form onSubmit={handleSubmit} className="bg-white border border-[#E5E5E5] p-6 space-y-4">
        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-[#52525B] block mb-1">Naam *</label>
          <input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))}
            className="w-full border border-[#E5E5E5] px-4 py-2.5 text-sm focus:border-[#2d8cf0] focus:outline-none"
            data-testid="contact-name" required />
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-[#52525B] block mb-1">E-mail *</label>
          <input value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))}
            type="email" className="w-full border border-[#E5E5E5] px-4 py-2.5 text-sm focus:border-[#2d8cf0] focus:outline-none"
            data-testid="contact-email" required />
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-[#52525B] block mb-1">Onderwerp</label>
          <input value={form.subject} onChange={e => setForm(p => ({...p, subject: e.target.value}))}
            className="w-full border border-[#E5E5E5] px-4 py-2.5 text-sm focus:border-[#2d8cf0] focus:outline-none"
            placeholder="Waar gaat je bericht over?" data-testid="contact-subject" />
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs font-bold uppercase tracking-wider text-[#52525B]">Bericht *</label>
            <VoiceInput
              onTranscript={(text) => setForm(p => ({ ...p, message: (p.message ? p.message + " " : "") + text }))}
              size="sm"
              title="Dicteer je bericht"
            />
          </div>
          <textarea value={form.message} onChange={e => setForm(p => ({...p, message: e.target.value}))}
            rows={5} className="w-full border border-[#E5E5E5] px-4 py-2.5 text-sm focus:border-[#2d8cf0] focus:outline-none resize-none"
            placeholder="Beschrijf je vraag of opmerking..." data-testid="contact-message" required />
        </div>
        <button type="submit" disabled={loading}
          className="w-full px-4 py-3 bg-[#2d8cf0] text-white font-bold text-sm hover:bg-[#1a6fd4] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="contact-submit-btn"
        >
          <Send className="w-4 h-4" /> {loading ? "Verzenden..." : "Verstuur Bericht"}
        </button>
      </form>

      <p className="text-xs text-center text-gray-400 mt-4">
        Je bericht wordt behandeld door ons AI-ondersteunde supportteam.
      </p>
    </div>
  );
};
